import { invariantResponse } from '@epic-web/invariant'
import { formatDistanceToNow } from 'date-fns'
import { data, Link, NavLink, useSubmit } from 'react-router'
import { GeneralErrorBoundary } from '#app/components/error-boundary.tsx'
import { floatingToolbarClassName } from '#app/components/floating-toolbar.tsx'
import { HoldToConfirmButton } from '#app/components/HoldToConfirmButton.tsx'
import { Button } from '#app/components/ui/button.tsx'
import { Icon } from '#app/components/ui/icon.tsx'
import { prisma } from '#app/utils/db.server.ts'
import { useOptionalAdminUser } from '#app/utils/user.ts'
import { type Route } from './+types/$date.ts'
import { DialogCloseSchedule } from './__close-schedule-dialog.tsx'
import { ScheduleActionButton } from './__schedule-action-button.tsx'

export { action } from '#app/routes/_app+/schedule+/actions.server.tsx'

export async function loader({ params }: Route.ActionArgs) {
	const schedule = await prisma.schedule.findFirst({
		select: {
			id: true,
			date: true,
			deadline: true,
			source: true,
			costPerHour: true,
			state: true,
			updatedAt: true,
		},
		where: { date: params.date },
	})
	invariantResponse(schedule, `Schedule not found for ${params.date}`, {
		status: 404,
	})

	const anythingOpen = await prisma.schedule.findFirst({
		select: { id: true },
		where: { state: 'open' },
	})
	const date = new Date(schedule.updatedAt)
	const timeAgo = formatDistanceToNow(date)

	const { _min, _max } = await prisma.userSchedule.aggregate({
		_min: { start: true },
		_max: { stop: true },
		where: { scheduleId: schedule.id },
	})

	return data({
		schedule,
		timeAgo,
		canOpen: !['open', 'closed'].includes(schedule.state) && !anythingOpen,
		canClose: schedule.state === 'locked' && !!_min.start && !!_max.stop,
	})
}

export default function ScheduleRoute({
	loaderData: { schedule, timeAgo, canOpen, canClose },
}: Route.ComponentProps) {
	const adminUser = useOptionalAdminUser()
	const submit = useSubmit()

	const handleSendEmailClicked = async (intent: string) => {
		await submit({ intent, scheduleId: schedule.id }, { method: 'POST' })
	}

	const state = schedule.state.toLowerCase()

	return (
		<div className="absolute inset-0 flex flex-col px-10">
			<div className="m-4 mb-0 text-xl">
				<em>
					Schedule Id:
					<strong> {schedule.id}</strong>
				</em>
			</div>
			<h2 className="text-h2 my-2 mt-2 lg:mb-6">
				Irrigation Schedule:<strong> {schedule.date}</strong>
			</h2>
			<div className="mb-6 overflow-y-auto">
				<p className="text-sm whitespace-break-spaces md:text-lg">
					Deadline for Sign-Up:<strong> {schedule.deadline}</strong>
				</p>
				<p className="text-sm whitespace-break-spaces capitalize md:text-lg">
					Water source:<strong> {schedule.source} Water</strong>
				</p>
				<p className="text-sm whitespace-break-spaces md:text-lg">
					Cost Per Hour:<strong> ${schedule.costPerHour}.00</strong>
				</p>
			</div>

			<div className={rowStyling('pending', 'open')}>
				<strong className="pb-1">Pending</strong>
				<div className="flex w-full flex-row gap-2">
					<OpenSignupButton disabled={state !== 'pending'} />
					<EditButton disabled={state !== 'pending'} />
					<DeleteButton disabled={state !== 'pending'} />
				</div>
			</div>

			<div className={rowStyling('open', 'locked')}>
				<strong className="pb-1">Open</strong>
				<div className="flex w-full flex-row gap-2 pb-2">
					<LockScheduleButton />
					<CancelButton disabled={state !== 'open'} />
					{/* <SendOpenEmailsButton disabled={state !== 'open'} /> */}
					<DeleteButton disabled={state !== 'open'} />
				</div>
				<div className="flex w-full flex-row justify-end gap-2">
					<SignupButton />
				</div>
			</div>

			<div className={rowStyling('locked', 'closed')}>
				<strong className="pb-1">Locked</strong>
				<div className="flex w-full flex-row gap-2 pb-2">
					<OpenSignupButton disabled={state !== 'locked'} />
					<GenerateButton disabled={state !== 'locked'} />
					<CloseScheduleButton disabled={state !== 'locked'} />
				</div>
				<div className="flex w-full flex-row justify-end gap-2">
					<TimelineButton />
				</div>
			</div>

			<div className={rowStyling('closed')}>
				<strong className="pb-1">Closed</strong>
				<div className="flex w-full flex-row gap-2 pb-2">
					{/* <DownloadCrossoversButton /> */}
					<QuickbooksButton disabled={state !== 'closed'} />
					<SendClosedEmailsButton disabled={state !== 'closed'} />
					<EditNotesButton disabled={state !== 'closed'} />
				</div>
				<div className="flex w-full flex-row justify-end gap-2">
					<CrossoversButton />
					<CreditsButton />
					<ViewNotesButton />
				</div>
			</div>

			<div className={floatingToolbarClassName}>
				<span className="text-foreground/90 text-sm max-[524px]:hidden">
					<Icon name="clock" className="scale-125">
						<span className="text-nowrap text-ellipsis">{timeAgo} ago</span>
					</Icon>
				</span>
			</div>
		</div>
	)

	function rowStyling(current: string, next?: string): string | undefined {
		return `flex w-full flex-col border-secondary-foreground pb-4 pt-1 px-2
					${state === current && 'rounded-lg border-[1.5px] bg-secondary/60'}
					${next && state !== next && 'border-b-[1.5px]'}
				`
	}

	function SignupButton() {
		return (
			<Button
				id={`signup-${schedule.id}`}
				variant="outline"
				className="md:w-40"
			>
				<NavLink to={`/schedule/${schedule.date}/signup`}>
					<Icon name="pencil-2" className="mr-1 scale-125 max-md:scale-150">
						<span className="max-md:hidden">Sign-up →</span>
					</Icon>
				</NavLink>
			</Button>
		)
	}

	function CrossoversButton() {
		return (
			<Button
				id={`crossovers-${schedule.id}`}
				variant="outline"
				className="lg:w-40"
			>
				<NavLink to={`/schedule/${schedule.date}/crossovers`}>
					<Icon name="calendar" className="mr-1 scale-125 max-md:scale-150">
						<span className="max-lg:hidden">Crossovers →</span>
					</Icon>
				</NavLink>
			</Button>
		)
	}

	function CreditsButton() {
		return (
			<Button
				id={`credits-${schedule.id}`}
				variant="outline"
				className="md:w-40"
			>
				<NavLink to={`/schedule/${schedule.date}/credits`}>
					<Icon name="credits" className="mr-1 scale-125 max-md:scale-150">
						<span className="max-md:hidden">Credits →</span>
					</Icon>
				</NavLink>
			</Button>
		)
	}

	function TimelineButton() {
		return (
			<Button
				id={`timeline-${schedule.id}`}
				variant="outline"
				className="md:w-40"
			>
				<NavLink to={`/schedule/${schedule.date}/timeline`}>
					<Icon name="calendar" className="mr-1 scale-125 max-md:scale-150">
						<span className="max-md:hidden">Timeline →</span>
					</Icon>
				</NavLink>
			</Button>
		)
	}

	function EditButton({ disabled }: { disabled: boolean }) {
		return (
			<Button
				id={`edit-${schedule.id}`}
				className="md:w-40"
				disabled={!adminUser || disabled}
			>
				<Link to="edit">
					<Icon name="pencil-1" className="scale-125 max-md:scale-150">
						<span className="max-md:hidden">Edit Schedule</span>
					</Icon>
				</Link>
			</Button>
		)
	}

	function GenerateButton({ disabled }: { disabled: boolean }) {
		return (
			<Button
				id={`generate-${schedule.id}`}
				className="md:w-40"
				disabled={!adminUser || disabled}
				variant="default"
			>
				<NavLink to={`/schedule/${schedule.date}/generate`}>
					<Icon name="activity-log" className="scale-125 max-md:scale-150">
						<span className="max-md:hidden">Generate</span>
					</Icon>
				</NavLink>
			</Button>
		)
	}

	function LockScheduleButton() {
		return (
			<ScheduleActionButton
				key={`lock-${schedule.id}`}
				scheduleId={schedule.id}
				icon="lock-closed"
				value="lock-schedule"
				text="Lock Scheduling"
				variant="secondary"
				className={
					state === 'open'
						? 'border-secondary-foreground border md:w-40'
						: 'md:w-40'
				}
				disabled={!adminUser || state !== 'open'}
			/>
		)
	}

	function OpenSignupButton({ disabled }: { disabled: boolean }) {
		return (
			<ScheduleActionButton
				key={`open-${schedule.id}`}
				scheduleId={schedule.id}
				icon="lock-open-1"
				value="open-schedule"
				text="Open Sign-up"
				variant="secondary"
				className={
					disabled ? 'md:w-40' : `border-secondary-foreground border md:w-40`
				}
				disabled={!adminUser || disabled || !canOpen}
			/>
		)
	}

	function CloseScheduleButton({ disabled }: { disabled: boolean }) {
		return (
			<DialogCloseSchedule
				key={`close-${schedule.id}`}
				scheduleId={schedule.id}
				className="md:w-40"
				disabled={!adminUser || disabled || !canClose}
			/>
		)
	}

	function CancelButton({ disabled }: { disabled: boolean }) {
		return (
			<HoldToConfirmButton
				key={`email-open-${schedule.id}`}
				className="md:w-40"
				variant="default"
				holdDelay={2000}
				disabled={!adminUser || disabled}
				onSubmit={() => handleSendEmailClicked('cancel-and-email')}
			>
				<Icon name="envelope-closed" className="scale-125 max-md:scale-150">
					<span className="text-nowrap text-ellipsis max-md:hidden">
						{'Cancel & Email'}
					</span>
				</Icon>
			</HoldToConfirmButton>
		)
	}

	function SendOpenEmailsButton({ disabled }: { disabled: boolean }) {
		return (
			<HoldToConfirmButton
				key={`email-open-${schedule.id}`}
				className="md:w-40"
				variant="default"
				holdDelay={2000}
				disabled={!adminUser || disabled}
				onSubmit={() => handleSendEmailClicked('open-emails')}
			>
				<Icon name="envelope-closed" className="scale-125 max-md:scale-150">
					<span className="text-nowrap text-ellipsis max-md:hidden">
						{'Send Emails'}
					</span>
				</Icon>
			</HoldToConfirmButton>
		)
	}

	function SendClosedEmailsButton({ disabled }: { disabled: boolean }) {
		return (
			<HoldToConfirmButton
				key={`email-closed-${schedule.id}`}
				className="md:w-40"
				variant="default"
				holdDelay={2000}
				disabled={!adminUser || disabled}
				onSubmit={() => handleSendEmailClicked('closed-emails')}
			>
				<Icon name="envelope-closed" className="scale-125 max-md:scale-150">
					<span className="text-nowrap text-ellipsis max-md:hidden">
						{'Send Emails'}
					</span>
				</Icon>
			</HoldToConfirmButton>
		)
	}

	function QuickbooksButton({ disabled }: { disabled: boolean }) {
		return (
			<Button className="md:w-40" disabled={!adminUser || disabled}>
				<Link
					reloadDocument
					to={`/resources/download/quickbooks/${schedule.date}`}
				>
					<Icon name="download">
						<span className="max-md:hidden">Quickbooks</span>
					</Icon>
				</Link>
			</Button>
		)
	}

	function DeleteButton({ disabled }: { disabled: boolean }) {
		return (
			<ScheduleActionButton
				key={`generate-${schedule.id}`}
				scheduleId={schedule.id}
				className="md:w-40"
				icon="trash"
				value="delete-schedule"
				text="Delete Schedule"
				variant="destructive"
				disabled={!adminUser || disabled}
			/>
		)
	}

	function ViewNotesButton() {
		return (
			<Button variant="outline" className="md:w-40">
				<NavLink to={`/documents/schedules/${schedule.date}`}>
					<Icon name="file-text" className="mr-1 scale-125 max-md:scale-150">
						<span className="max-md:hidden">Notes →</span>
					</Icon>
				</NavLink>
			</Button>
		)
	}

	function EditNotesButton({ disabled }: { disabled: boolean }) {
		return (
			<Button disabled={!adminUser || disabled} className="md:w-40">
				<Link
					to={`/keystatic/branch/main/collection/schedules/item/${schedule.date}`}
				>
					<Icon name="pencil-1" className="scale-125 max-md:scale-150">
						<span className="max-md:hidden">Edit Notes</span>
					</Icon>
				</Link>
			</Button>
		)
	}
}

export const meta: Route.MetaFunction = ({ params }) => {
	return [
		{ title: `Irrigation Schedules | ${params.date}` },
		{
			name: 'description',
			content: `Clearwater Farms 1 Irrigation Schedules`,
		},
	]
}

export function ErrorBoundary() {
	return (
		<GeneralErrorBoundary
			statusHandlers={{
				403: () => <p>You are not allowed to do that</p>,
				404: ({ params }) => (
					<p>No schedule with the id "{params.scheduleId}" exists</p>
				),
				406: () => <p>Only one schedule can be open at a time.</p>,
			}}
		/>
	)
}
